import { IServicePartner } from "@rumble-it/common-lib/src/types/ServicePartner";
import { Button } from "./Button";
import { Tag, theme } from "antd";
import { StarFilled } from "@ant-design/icons";

const { useToken } = theme;

export interface HeaderImageProps {
  height?: number;
  minHeight?: number;
  maxHeight?: number;
}

export const ServicePartnerCard = ({
  servicePartner,
  isSelected,
  onClick,
  headerImageProps,
  preferredPartnerText,
}: {
  servicePartner: IServicePartner;
  isSelected: boolean;
  onClick: (servicePartner: IServicePartner) => void;
  headerImageProps?: HeaderImageProps; // Props for the service partner header image, e.g. height of the header image in pixels.
  preferredPartnerText?: string;
}) => {
  const addressString =
    servicePartner.address && servicePartner.address.street
      ? `${servicePartner.address.street} ${servicePartner.address.streetNumber}${servicePartner.address.streetNumberAddition || ""}, ${servicePartner.address.postalCode} ${servicePartner.address.city}`
      : null;

  const { token } = useToken();

  return (
    <div
      className={`bg-white rounded-xl p-2`}
      style={
        servicePartner.preferredPartner
          ? {
              border: `5px solid ${token.colorPrimary}`,
            }
          : {}
      }
    >
      {servicePartner.headerImageContentUrl ? (
        <div
          className="rounded-lg pb-3 flex items-center justify-center"
          style={{
            height: headerImageProps?.maxHeight
              ? headerImageProps?.maxHeight
              : headerImageProps?.height
                ? `${headerImageProps.height}px`
                : undefined,
            minHeight: headerImageProps?.minHeight
              ? `${headerImageProps.minHeight}px`
              : undefined,
            maxHeight: headerImageProps?.maxHeight
              ? `${headerImageProps.maxHeight}px`
              : 100,
          }}
        >
          <img
            src={servicePartner.headerImageContentUrl}
            alt={
              servicePartner.fullName ||
              servicePartner.name ||
              "Service Partner"
            }
            className="object-cover w-full h-full rounded-lg"
            style={{
              maxHeight: headerImageProps?.maxHeight
                ? `${headerImageProps.maxHeight}`
                : 100,
              minHeight: headerImageProps?.minHeight
                ? `${headerImageProps.minHeight}`
                : undefined,
              height: headerImageProps?.maxHeight
                ? headerImageProps?.maxHeight
                : headerImageProps?.height
                  ? `${headerImageProps.height}`
                  : undefined,
            }}
          />
        </div>
      ) : null}

      <div className="p-3">
        <div className="flex items-center">
          <span className="w-7 flex items-center justify-center mr-4">
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="stroke-black"
                d="M3.6 9.54015C2.30405 10.1118 1.5 10.9085 1.5 11.7897C1.5 13.5294 4.63401 14.9397 8.5 14.9397C12.366 14.9397 15.5 13.5294 15.5 11.7897C15.5 10.9085 14.696 10.1118 13.4 9.54015M12.7 5.1397C12.7 7.98429 9.55 9.3397 8.5 11.4397C7.45 9.3397 4.3 7.98429 4.3 5.1397C4.3 2.8201 6.1804 0.939697 8.5 0.939697C10.8196 0.939697 12.7 2.8201 12.7 5.1397ZM9.2 5.1397C9.2 5.5263 8.8866 5.8397 8.5 5.8397C8.1134 5.8397 7.8 5.5263 7.8 5.1397C7.8 4.7531 8.1134 4.4397 8.5 4.4397C8.8866 4.4397 9.2 4.7531 9.2 5.1397Z"
                stroke="#001A40"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <circle
                className="fill-secondary-default"
                cx="8.49997"
                cy="5.11819"
                r="1.8215"
                fill="#FFCC00"
              ></circle>
            </svg>
          </span>

          <div>
            <h3 className="text-sm leading-6 font-extrabold">
              {servicePartner.fullName || servicePartner.name}
            </h3>

            {addressString ? (
              <p className="text-xs text-gray-500">{addressString}</p>
            ) : null}
          </div>

          {servicePartner.preferredPartner ? (
            <Tag
              color="primary"
              className="ml-auto font-medium"
              icon={<StarFilled />}
            >
              {preferredPartnerText}
            </Tag>
          ) : null}
        </div>

        {servicePartner.travelDistanceInMeter !== undefined &&
        servicePartner.travelDistanceInMeter !== null ? (
          <div className="flex items-center mt-3">
            <span className="w-7 flex items-center justify-center mr-4">
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="stroke-black"
                  d="M8.15 3.0397H8.45408C10.5871 3.0397 11.6537 3.0397 12.0585 3.4228C12.4085 3.75395 12.5636 4.24179 12.4691 4.71424C12.3598 5.26081 11.4891 5.8767 9.7476 7.10847L6.9024 9.12092C5.16093 10.3527 4.29019 10.9686 4.18088 11.5151C4.0864 11.9876 4.24149 12.4754 4.59145 12.8066C4.99631 13.1897 6.06285 13.1897 8.19592 13.1897H8.85M5.7 3.0397C5.7 4.1995 4.7598 5.1397 3.6 5.1397C2.4402 5.1397 1.5 4.1995 1.5 3.0397C1.5 1.8799 2.4402 0.939697 3.6 0.939697C4.7598 0.939697 5.7 1.8799 5.7 3.0397ZM15.5 12.8397C15.5 13.9995 14.5598 14.9397 13.4 14.9397C12.2402 14.9397 11.3 13.9995 11.3 12.8397C11.3 11.6799 12.2402 10.7397 13.4 10.7397C14.5598 10.7397 15.5 11.6799 15.5 12.8397Z"
                  stroke="#001A40"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  className="stroke-secondary-default"
                  d="M13.4 14.9397C14.5598 14.9397 15.5 13.9995 15.5 12.8397C15.5 11.6799 14.5598 10.7397 13.4 10.7397C12.2403 10.7397 11.3 11.6799 11.3 12.8397C11.3 13.9995 12.2403 14.9397 13.4 14.9397Z"
                  stroke="#FFCC00"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </span>
            <div>
              <p className="text-[13px] leading-6">
                <strong>Reisafstand:</strong>{" "}
                {Math.round(servicePartner.travelDistanceInMeter / 1000)} km
              </p>
            </div>
          </div>
        ) : servicePartner.sphericalDistanceInMeter !== undefined &&
          servicePartner.sphericalDistanceInMeter !== null ? (
          <div className="flex items-center mt-3">
            <span className="w-7 flex items-center justify-center mr-4">
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="stroke-black"
                  d="M8.15 3.0397H8.45408C10.5871 3.0397 11.6537 3.0397 12.0585 3.4228C12.4085 3.75395 12.5636 4.24179 12.4691 4.71424C12.3598 5.26081 11.4891 5.8767 9.7476 7.10847L6.9024 9.12092C5.16093 10.3527 4.29019 10.9686 4.18088 11.5151C4.0864 11.9876 4.24149 12.4754 4.59145 12.8066C4.99631 13.1897 6.06285 13.1897 8.19592 13.1897H8.85M5.7 3.0397C5.7 4.1995 4.7598 5.1397 3.6 5.1397C2.4402 5.1397 1.5 4.1995 1.5 3.0397C1.5 1.8799 2.4402 0.939697 3.6 0.939697C4.7598 0.939697 5.7 1.8799 5.7 3.0397ZM15.5 12.8397C15.5 13.9995 14.5598 14.9397 13.4 14.9397C12.2402 14.9397 11.3 13.9995 11.3 12.8397C11.3 11.6799 12.2402 10.7397 13.4 10.7397C14.5598 10.7397 15.5 11.6799 15.5 12.8397Z"
                  stroke="#001A40"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  className="stroke-secondary-default"
                  d="M13.4 14.9397C14.5598 14.9397 15.5 13.9995 15.5 12.8397C15.5 11.6799 14.5598 10.7397 13.4 10.7397C12.2403 10.7397 11.3 11.6799 11.3 12.8397C11.3 13.9995 12.2403 14.9397 13.4 14.9397Z"
                  stroke="#FFCC00"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </span>
            <div>
              <p className="text-[13px] leading-6">
                <strong>Afstand:</strong>{" "}
                {Math.round(servicePartner.sphericalDistanceInMeter / 1000)} km
              </p>
            </div>
          </div>
        ) : null}

        {servicePartner.travelTimeInMinutes !== undefined &&
        servicePartner.travelTimeInMinutes !== null ? (
          <div className="flex items-center mt-3">
            <span className="w-7 flex items-center justify-center mr-4">
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="stroke-secondary-default"
                  d="M8.5 3.73969V7.93968L11.3 9.33968"
                  stroke="#FFCC00"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  className="stroke-black"
                  d="M8.5 14.9397C12.366 14.9397 15.5 11.8057 15.5 7.9397C15.5 4.0737 12.366 0.939697 8.5 0.939697C4.63401 0.939697 1.5 4.0737 1.5 7.9397C1.5 11.8057 4.63401 14.9397 8.5 14.9397Z"
                  stroke="#001A40"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </span>
            <div>
              <p className="text-[13px] leading-6">
                <strong>Reistijd:</strong> {servicePartner.travelTimeInMinutes}{" "}
                min
              </p>
            </div>
          </div>
        ) : null}

        {servicePartner.uniqueSellingPoints &&
        servicePartner.uniqueSellingPoints.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 pb-3">
            {servicePartner.uniqueSellingPoints.map((usp) => (
              <div
                key={`usp-${servicePartner.id}-${usp}`}
                className="flex items-center border border-solid border-gray-100 py-1.5 rounded-xl"
              >
                <span className="min-w-6 flex justify-center text-sm font-bold text-green-500 mr-1">
                  ✓
                </span>
                <p className="text-xs font-bold leading-4 hyphens-auto">
                  {usp}
                </p>
              </div>
            ))}
          </div>
        ) : null}

        <Button
          type="primary"
          className={`w-full mt-3${isSelected ? " bg-green-500 text-white border-green-500" : ""}`}
          onClick={() => onClick(servicePartner)}
        >
          {isSelected ? "Geselecteerd" : "Selecteer"}
        </Button>
      </div>
    </div>
  );
};
